import { Download, FileQuestion } from 'lucide-react';
import { useState } from 'react';
import { Button } from '../../../../components/Button';
import { EmptyState } from '../../../../components/EmptyState';
import { ErrorState } from '../../../../components/ErrorState';
import { Modal } from '../../../../components/Modal';
import { PermissionGate } from '../../../../auth/PermissionGate';
import { Skeleton } from '../../../../components/Skeleton';
import { ApiError } from '../../../../lib/apiError';
import { formatBytes } from '../../../../lib/formatBytes';
import { formatFileType } from '../../../../lib/formatFileType';
import { repositoryApi } from '../repositoryApi';
import { useDocumentPreview } from '../useDocumentPreview';
import type {
  RepositoryDocumentVersionListItem,
  RepositoryItemListItem,
} from '../repositoryTypes';

/**
 * One ARCHIVED version, previewed and downloadable — opened from the file in the history.
 *
 * **A superseded file is RETAINED.** Replacing a document archives its current file at the
 * number it was carrying and points the record at the new bytes; nothing destroys what it
 * superseded. Until this existed there was no way to reach one, so the Versions tab listed
 * filenames a reader could not open — which is the dead affordance the hide-rule forbids,
 * arriving as a column rather than as a button.
 *
 * **It carries NO confirm tick**, like `<ImagePreviewModal>` and for the same reason: the
 * confirm rule gates the primary action of a TRANSACTION, and this modal writes nothing. A tick
 * in front of "look at a file" is ceremony that teaches readers to click past ticks.
 *
 * **Everything that makes a preview safe is the hook's**, not this component's — the verified
 * media type, the allow-list of inert renderings, and the blob pinned to that type on
 * construction. See `useDocumentPreview`; a second implementation here for versions is exactly
 * the drift one shared hook exists to prevent.
 */
export function DocumentVersionModal({
  open,
  document,
  version,
  onClose,
}: {
  open: boolean;
  document: RepositoryItemListItem;
  version: RepositoryDocumentVersionListItem;
  onClose: () => void;
}) {
  const { kind, url, loading, error } = useDocumentPreview(
    document.id,
    version.fileContentType,
    version.id,
  );

  const [downloadError, setDownloadError] = useState<ApiError | null>(null);

  /**
   * Fetches the archived file and hands it to the browser.
   *
   * **An object URL, not an anchor pointed at the endpoint** — a browser navigation carries no
   * bearer token, so a bare href arrives unauthenticated and fails silently. The same pairing
   * the profile's own download uses.
   *
   * It does NOT reuse the preview's blob: the preview is only built for a renderable type, so
   * a version the modal cannot show would have nothing to hand over — and that is precisely
   * the case a reader reaches for Download in.
   */
  async function download() {
    setDownloadError(null);

    try {
      const { blob, fileName } = await repositoryApi.downloadVersion(document.id, version.id);

      const objectUrl = URL.createObjectURL(blob);
      const anchor = window.document.createElement('a');

      anchor.href = objectUrl;
      anchor.download = fileName ?? version.fileOriginalName ?? document.name;
      window.document.body.appendChild(anchor);
      anchor.click();
      anchor.remove();

      URL.revokeObjectURL(objectUrl);
    } catch (caught) {
      setDownloadError(
        caught instanceof ApiError ? caught : new ApiError('Could not download this version.', 0),
      );
    }
  }

  /*
    What the reader is looking at, in the title — the VERSION, because that is what they clicked
    and the document's own name is already on the page behind this.
  */
  const facts = [
    formatFileType(version.fileContentType),
    version.fileSizeBytes === null ? null : formatBytes(version.fileSizeBytes),
    version.fileOriginalName,
  ].filter((value): value is string => Boolean(value));

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`Version ${version.versionNumber}`}
      /*
        `3xl`, because the content is a page of document rather than a form. The preview panel
        on the profile gets the whole height of the page; this is the same content in a dialog,
        so it takes the widest step a modal has.
      */
      size="3xl"
      id="repository-version-modal"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="secondary" onClick={onClose}>
            Close
          </Button>

          {/*
            `repository.download` — the key the endpoint behind it carries. An archived file is
            not a lesser secret than the one that replaced it.
          */}
          <PermissionGate permission="repository.download">
            <Button
              id="repository-version-download"
              variant="primary"
              leading={<Download className="h-4 w-4" aria-hidden="true" />}
              onClick={() => void download()}
            >
              Download
            </Button>
          </PermissionGate>
        </div>
      }
    >
      <div className="flex flex-col gap-3">
        {/*
          One middot-joined line of what this version IS, above the viewer — the type, the size
          and the name it was uploaded under. The uploaded name lives HERE rather than in the
          title, because the title is the version and two versions of one document routinely
          share a filename.
        */}
        {facts.length > 0 ? (
          <p className="text-xs text-slate-500">{facts.join(' · ')}</p>
        ) : null}

        {downloadError ? (
          <p role="alert" className="text-sm text-red-700">
            {downloadError.message}
          </p>
        ) : null}

        {/*
          A fixed viewer height rather than one that grows with the content. A modal whose body
          is as tall as a PDF would push its own footer — and the Download button in it — off
          the screen, which is the one control a reader reaches for when the preview is no use
          to them.
        */}
        <div className="flex h-[60vh] min-h-0 flex-col">
          {kind === 'none' ? (
            <div className="flex h-full min-h-0 flex-col items-center justify-center gap-4">
              <FileQuestion className="h-10 w-10 text-slate-300" aria-hidden="true" />
              <EmptyState
                minHeight="min-h-0"
                message={
                  version.fileContentType
                    ? `This file is ${version.fileContentType}, which cannot be shown here. Download it to open it in the application it belongs to.`
                    : 'This file cannot be shown here. Download it to open it in the application it belongs to.'
                }
              />
            </div>
          ) : loading ? (
            <Skeleton className="h-full w-full" />
          ) : error || !url ? (
            <div className="flex h-full min-h-0 flex-col items-center justify-center">
              <ErrorState message={error?.message ?? 'Could not load this version.'} />
            </div>
          ) : kind === 'image' ? (
            <div className="modern-scrollbar flex h-full min-h-0 items-start justify-center overflow-auto rounded-lg bg-slate-50 p-4">
              <img
                src={url}
                alt={version.fileOriginalName ?? `Version ${version.versionNumber}`}
                className="max-w-full rounded-lg border border-slate-200 bg-white shadow-sm"
              />
            </div>
          ) : (
            /*
              An `<iframe>` rather than an `<object>`, for the reason the profile's preview gives:
              it is the element browsers agree to route a blob URL through to their built-in PDF
              viewer, and its failure mode is a blank frame rather than a download prompt.
            */
            <iframe
              src={url}
              title={`Version ${version.versionNumber}`}
              className="h-full w-full rounded-lg border border-slate-200"
            />
          )}
        </div>
      </div>
    </Modal>
  );
}
